// ============================================================
// FILE: MyBookingLink.js
// LOCATION: C:\gardenmarket\web\src\components\MyBookingLink.js
// ============================================================
// PURPOSE:
//   Shows a gardener their personal booking link and makes it
//   easy to share.
//
// WHY THIS MATTERS MORE THAN IT LOOKS:
//   This is the answer to the cold-start problem. Recruiting
//   gardeners with "join my marketplace" asks them to bet on
//   demand that does not exist yet. Handing them a free booking
//   tool for the customers they ALREADY have is useful on day
//   one, with no dependency on marketplace traffic — and those
//   customers arrive on the platform as a side effect.
//
//   The exclusivity promise is what makes it safe to share: a
//   job posted through this link goes only to them, permanently.
//   Without that guarantee no gardener would hand it out.
// ============================================================

import { useState } from 'react';

export default function MyBookingLink({ referralCode, firstName }) {
    const [copied, setCopied] = useState(false);

    if (!referralCode) return null;

    const url = `${window.location.origin}/g/${referralCode}`;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch {
            // Clipboard API needs HTTPS and a user gesture; it can
            // still fail. Select the text so they can copy manually.
            const el = document.getElementById('gh-booking-link');
            if (el) {
                const range = document.createRange();
                range.selectNodeContents(el);
                const sel = window.getSelection();
                sel.removeAllRanges();
                sel.addRange(range);
            }
        }
    };

    // Pre-written so a gardener does not have to compose it. Most
    // will paste this straight into WhatsApp or a text.
    const shareText =
        `Hi, you can book me and see my quotes here: ${url}`;

    const smsHref = `sms:?&body=${encodeURIComponent(shareText)}`;

    return (
        <div style={styles.card}>
            <h3 style={styles.title}>Your booking link</h3>

            <p style={styles.blurb}>
                Give this to your own customers. Jobs they post through it
                come <strong>straight to you</strong> — no other gardener
                sees them, ever.
            </p>

            <div style={styles.linkRow}>
                <code id="gh-booking-link" style={styles.link}>{url}</code>
            </div>

            <div style={styles.actions}>
                <button
                    type="button"
                    onClick={copy}
                    style={copied ? styles.btnCopied : styles.btn}
                    className="gh-touch-target"
                >
                    {copied ? '✓ Copied' : 'Copy link'}
                </button>
                <a
                    href={smsHref}
                    style={styles.btnSecondary}
                    className="gh-touch-target"
                >
                    Send by text
                </a>
            </div>

            <p style={styles.fine}>
                Put it on your quotes, business cards, ute, or Facebook page.
                You still pay the normal small fee when a job is accepted.
            </p>
        </div>
    );
}

const styles = {
    card:         { background:'white', borderRadius:'10px', padding:'20px',
                    boxShadow:'0 2px 8px rgba(0,0,0,0.07)', marginBottom:'16px' },
    title:        { fontSize:'1rem', fontWeight:'bold', color:'#2d6a4f',
                    margin:'0 0 10px' },
    blurb:        { fontSize:'0.88rem', color:'#555', lineHeight:1.6,
                    marginTop:0, marginBottom:'14px' },
    linkRow:      { background:'#f5f7f6', border:'1px solid #dde5e1',
                    borderRadius:'6px', padding:'10px 12px', marginBottom:'12px',
                    overflowX:'auto' },
    link:         { fontSize:'0.85rem', color:'#2d6a4f', fontFamily:
                    'ui-monospace, Menlo, Consolas, monospace', whiteSpace:'nowrap' },
    actions:      { display:'flex', gap:'8px', flexWrap:'wrap' },
    btn:          { padding:'10px 18px', backgroundColor:'#2d6a4f', color:'white',
                    border:'none', borderRadius:'6px', fontSize:'0.88rem',
                    fontWeight:600, cursor:'pointer' },
    btnCopied:    { padding:'10px 18px', backgroundColor:'#14532D', color:'white',
                    border:'none', borderRadius:'6px', fontSize:'0.88rem',
                    fontWeight:600, cursor:'pointer' },
    btnSecondary: { padding:'10px 18px', backgroundColor:'white', color:'#2d6a4f',
                    border:'1px solid #ddd', borderRadius:'6px', fontSize:'0.88rem',
                    fontWeight:600, textDecoration:'none', display:'inline-flex',
                    alignItems:'center' },
    fine:         { fontSize:'0.78rem', color:'#999', lineHeight:1.6,
                    marginTop:'12px', marginBottom:0 },
};
